import React, { Suspense, lazy, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { BookingProvider } from "./context/BookingContext";
import { LanguageProvider } from "./context/LanguageContext";
import { MaintenanceOverlay } from "./components/MaintenanceOverlay";
import { AuthProvider } from "./context/AuthContext";
import HomePage from "./pages/site/HomePage";
import SitePage from "./pages/site/SitePage";
import RoomPage from "./pages/site/RoomPage";
import NotFoundPage from "./pages/site/NotFoundPage";
import { LEGAL_SLUGS } from "./i18n/legal/meta";
import { LANGS, SLUGS } from "./utils/siteNav";

// After a deploy the old index.html may reference chunks that no longer exist — reload once instead of a white screen.
const lazyRetry = (loader) => lazy(() => loader().then((m) => { sessionStorage.removeItem("chunk-reload"); return m; }).catch((err) => {
  if (!sessionStorage.getItem("chunk-reload")) {
    sessionStorage.setItem("chunk-reload", "1");
    window.location.reload();
    return new Promise(() => {});
  }
  throw err;
}));

// Secondary public pages carry their own 8-language dictionaries → separate chunks
const GuidesIndexPage = lazyRetry(() => import("./pages/site/GuidesIndexPage"));
const GuidePage = lazyRetry(() => import("./pages/site/GuidePage"));
const LegalPage = lazyRetry(() => import("./pages/site/LegalPage"));

// Admin (lazy-loaded so the public website ships a small bundle)
const AdminLayout = lazyRetry(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazyRetry(() => import("./components/admin/AdminDashboard"));
const AdminBookings = lazyRetry(() => import("./components/admin/AdminBookings"));
const AdminRooms = lazyRetry(() => import("./components/admin/AdminRooms"));
const AdminGallery = lazyRetry(() => import("./components/admin/AdminGallery"));
const AdminTestimonials = lazyRetry(() => import("./components/admin/AdminTestimonials"));
const AdminAnalytics = lazyRetry(() => import("./components/admin/AdminAnalytics"));
const AdminUsers = lazyRetry(() => import("./components/admin/AdminUsers"));
const AdminReporting = lazyRetry(() => import("./components/admin/AdminReporting"));
const AdminRecovery = lazyRetry(() => import("./components/admin/AdminRecovery"));
const AdminGuestEmails = lazyRetry(() => import("./components/admin/AdminGuestEmails"));
const AdminChatConversations = lazyRetry(() => import("./components/admin/AdminChatConversations"));
const AdminOperations = lazyRetry(() => import("./components/admin/AdminOperations"));
const AdminHousekeeping = lazyRetry(() => import("./components/admin/AdminHousekeeping"));
const AdminFeedback = lazyRetry(() => import("./components/admin/AdminFeedback"));
const AdminForecast = lazyRetry(() => import("./components/admin/AdminForecast"));
const AdminMaintenance = lazyRetry(() => import("./components/admin/AdminMaintenance"));
const AdminDigest = lazyRetry(() => import("./components/admin/AdminDigest"));
const AdminSeoWatchdog = lazyRetry(() => import("./components/admin/AdminSeoWatchdog"));
const AdminWebsiteStatus = lazyRetry(() => import("./components/admin/AdminWebsiteStatus"));
const AdminUpsell = lazyRetry(() => import("./components/admin/AdminUpsell"));
const ChangePasswordPage = lazyRetry(() => import("./components/admin/ChangePasswordPage"));
const AdminActivity = lazyRetry(() => import("./components/admin/AdminActivity"));
const AdminWelcome = lazyRetry(() => import("./components/admin/AdminWelcome"));
const AdminReviews = lazyRetry(() => import("./components/admin/AdminReviews"));
const AdminPrivateFeedback = lazyRetry(() => import("./components/admin/AdminPrivateFeedback"));
const AdminInquiries = lazyRetry(() => import("./components/admin/AdminInquiries"));
const AdminRevenue = lazyRetry(() => import("./components/admin/AdminRevenue"));
const AdminRoster = lazyRetry(() => import("./components/admin/AdminRoster"));
const AdminRequests = lazyRetry(() => import("./components/admin/AdminRequests"));
const AdminRoomSurveys = lazyRetry(() => import("./components/admin/AdminRoomSurveys"));
const AdminQRPrint = lazyRetry(() => import("./components/admin/AdminQRPrint"));
const AdminQRAnalytics = lazyRetry(() => import("./components/admin/AdminQRAnalytics"));
const AdminQRABTest = lazyRetry(() => import("./components/admin/AdminQRABTest"));
const AdminCalendar = lazyRetry(() => import("./components/admin/AdminCalendar"));
const AdminAutopilot = lazyRetry(() => import("./components/admin/AdminAutopilot"));
const AdminGuests = lazyRetry(() => import("./components/admin/AdminGuests"));
const AdminInvoices = lazyRetry(() => import("./components/admin/AdminInvoices"));
const AdminHotelSettings = lazyRetry(() => import("./components/admin/AdminHotelSettings"));
const AdminEfiSettings = lazyRetry(() => import("./components/admin/AdminEfiSettings"));
const AdminPrinting = lazyRetry(() => import("./components/admin/AdminPrinting"));
const AdminNightAudit = lazyRetry(() => import("./components/admin/AdminNightAudit"));
const AdminGuestBook = lazyRetry(() => import("./components/admin/AdminGuestBook"));
const AdminGroups = lazyRetry(() => import("./components/admin/AdminGroups"));
const AdminCashup = lazyRetry(() => import("./components/admin/AdminCashup"));
const AdminTasks = lazyRetry(() => import("./components/admin/AdminTasks"));
const AdminDining = lazyRetry(() => import("./components/admin/AdminDining"));
const AdminKitchen = lazyRetry(() => import("./components/admin/AdminKitchen"));
const AdminTelegram = lazyRetry(() => import("./components/admin/AdminTelegram"));
const AdminRoomHistory = lazyRetry(() => import("./components/admin/AdminRoomHistory"));
const AdminVouchers = lazyRetry(() => import("./components/admin/AdminVouchers"));
const AdminArrivals = lazyRetry(() => import("./components/admin/AdminArrivals"));
import PermissionRoute from "./components/admin/PermissionRoute";

// Guest-only pages (lazy)
const FeedbackForm = lazyRetry(() => import("./pages/FeedbackForm"));
const GuestDirectory = lazyRetry(() => import("./pages/GuestDirectory"));
const UpsellPage = lazyRetry(() => import("./pages/UpsellPage"));
const DepositPage = lazyRetry(() => import("./pages/DepositPage"));
const PrecheckinPage = lazyRetry(() => import("./pages/PrecheckinPage"));
const DepositPayPage = lazyRetry(() => import("./pages/DepositPayPage"));
const ReviewPage = lazyRetry(() => import("./pages/ReviewPage"));
const RoomHome = lazyRetry(() => import("./pages/RoomQR").then((m) => ({ default: m.RoomHome })));
const RoomRequest = lazyRetry(() => import("./pages/RoomQR").then((m) => ({ default: m.RoomRequest })));
const RoomSurvey = lazyRetry(() => import("./pages/RoomQR").then((m) => ({ default: m.RoomSurvey })));
const QRRedirect = lazyRetry(() => import("./pages/RoomQR").then((m) => ({ default: m.QRRedirect })));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="route-loading">
    <div className="w-8 h-8 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" />
  </div>
);

function App() {
  // Inject Google Analytics 4 if a valid measurement ID is configured
  useEffect(() => {
    const gaId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
    if (!gaId || gaId === 'G-PLACEHOLDER' || document.getElementById('ga4-script')) return;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', gaId, { send_page_view: true });
    // Load the tag once the browser is idle — analytics must not compete with the first paint.
    const load = () => {
      if (document.getElementById('ga4-script')) return;
      const script = document.createElement('script');
      script.id = 'ga4-script';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(script);
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(load, { timeout: 4000 });
    else setTimeout(load, 2500);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <BookingProvider>
            <AuthProvider>
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Public site: one page per main-menu section, in every language (EN at root, others under /:lang) */}
                {LANGS.map((lang) => {
                  const base = lang === 'en' ? '' : `/${lang}`;
                  return [
                    <Route key={`${lang}-home`} path={base || '/'} element={<HomePage />} />,
                    ...SLUGS.map((slug) => <Route key={`${lang}-${slug}`} path={`${base}/${slug}`} element={<SitePage slug={slug} />} />),
                    <Route key={`${lang}-room`} path={`${base}/rooms/:roomSlug`} element={<RoomPage />} />,
                    <Route key={`${lang}-guides`} path={`${base}/guides`} element={<GuidesIndexPage />} />,
                    <Route key={`${lang}-guide`} path={`${base}/guides/:guideSlug`} element={<GuidePage />} />,
                    ...LEGAL_SLUGS.map((slug) => <Route key={`${lang}-${slug}`} path={`${base}/${slug}`} element={<LegalPage slug={slug} />} />),
                  ];
                })}

                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<PermissionRoute perm="dashboard"><AdminDashboard /></PermissionRoute>} />
                  <Route path="bookings" element={<PermissionRoute perm="bookings"><AdminBookings /></PermissionRoute>} />
                  <Route path="rooms" element={<PermissionRoute perm="rooms"><AdminRooms /></PermissionRoute>} />
                  <Route path="gallery" element={<PermissionRoute perm="gallery"><AdminGallery /></PermissionRoute>} />
                  <Route path="testimonials" element={<PermissionRoute perm="testimonials"><AdminTestimonials /></PermissionRoute>} />
                  <Route path="analytics" element={<PermissionRoute perm="analytics"><AdminAnalytics /></PermissionRoute>} />
                  <Route path="reporting" element={<PermissionRoute perm="reporting"><AdminReporting /></PermissionRoute>} />
                  <Route path="recovery" element={<PermissionRoute perm="recovery"><AdminRecovery /></PermissionRoute>} />
                  <Route path="guest-emails" element={<PermissionRoute perm="recovery"><AdminGuestEmails /></PermissionRoute>} />
                  <Route path="chats" element={<PermissionRoute perm="chats"><AdminChatConversations /></PermissionRoute>} />
                  <Route path="operations" element={<PermissionRoute perm="today"><AdminOperations /></PermissionRoute>} />
                  <Route path="arrivals" element={<PermissionRoute perm="today"><AdminArrivals /></PermissionRoute>} />
                  <Route path="housekeeping" element={<PermissionRoute perm="housekeeping"><AdminHousekeeping /></PermissionRoute>} />
                  <Route path="feedback" element={<PermissionRoute perm="feedback"><AdminFeedback /></PermissionRoute>} />
                  <Route path="forecast" element={<PermissionRoute perm="forecast"><AdminForecast /></PermissionRoute>} />
                  <Route path="maintenance" element={<PermissionRoute perm="maintenance"><AdminMaintenance /></PermissionRoute>} />
                  <Route path="digest" element={<PermissionRoute perm="digest"><AdminDigest /></PermissionRoute>} />
                  <Route path="seo-watchdog" element={<PermissionRoute perm="analytics"><AdminSeoWatchdog /></PermissionRoute>} />
                  <Route path="website" element={<PermissionRoute perm="users"><AdminWebsiteStatus /></PermissionRoute>} />
                  <Route path="upsell" element={<PermissionRoute perm="upsell"><AdminUpsell /></PermissionRoute>} />
                  <Route path="welcome" element={<PermissionRoute perm="welcome"><AdminWelcome /></PermissionRoute>} />
                  <Route path="reviews" element={<PermissionRoute perm="reviews"><AdminReviews /></PermissionRoute>} />
                  <Route path="private-feedback" element={<PermissionRoute perm="private_feedback"><AdminPrivateFeedback /></PermissionRoute>} />
                  <Route path="inquiries" element={<PermissionRoute perm="inquiries"><AdminInquiries /></PermissionRoute>} />
                  <Route path="revenue" element={<PermissionRoute perm="revenue"><AdminRevenue /></PermissionRoute>} />
                  <Route path="roster" element={<PermissionRoute perm="roster"><AdminRoster /></PermissionRoute>} />
                  <Route path="requests" element={<PermissionRoute perm="requests"><AdminRequests /></PermissionRoute>} />
                  <Route path="room-surveys" element={<PermissionRoute perm="surveys"><AdminRoomSurveys /></PermissionRoute>} />
                  <Route path="qr-print" element={<PermissionRoute perm="surveys"><AdminQRPrint /></PermissionRoute>} />
                  <Route path="qr-analytics" element={<PermissionRoute perm="qr_analytics"><AdminQRAnalytics /></PermissionRoute>} />
                  <Route path="qr-ab" element={<PermissionRoute perm="qr_analytics"><AdminQRABTest /></PermissionRoute>} />
                  <Route path="calendar" element={<PermissionRoute perm="calendar"><AdminCalendar /></PermissionRoute>} />
                  <Route path="autopilot" element={<PermissionRoute perm="revenue"><AdminAutopilot /></PermissionRoute>} />
                  <Route path="guests" element={<PermissionRoute perm="guests"><AdminGuests /></PermissionRoute>} />
                  <Route path="invoices" element={<PermissionRoute perm="bookings"><AdminInvoices /></PermissionRoute>} />
                  <Route path="hotel-settings" element={<PermissionRoute perm="users"><AdminHotelSettings /></PermissionRoute>} />
                  <Route path="efi" element={<PermissionRoute perm="users"><AdminEfiSettings /></PermissionRoute>} />
                  <Route path="printing" element={<PermissionRoute perm="users"><AdminPrinting /></PermissionRoute>} />
                  <Route path="night-audit" element={<PermissionRoute perm="night_audit"><AdminNightAudit /></PermissionRoute>} />
                  <Route path="guest-book" element={<PermissionRoute perm="guest_book"><AdminGuestBook /></PermissionRoute>} />
                  <Route path="groups" element={<PermissionRoute perm="folio"><AdminGroups /></PermissionRoute>} />
                  <Route path="cash-up" element={<PermissionRoute perm="folio"><AdminCashup /></PermissionRoute>} />
                  <Route path="tasks" element={<PermissionRoute perm="tasks"><AdminTasks /></PermissionRoute>} />
                  <Route path="dining" element={<PermissionRoute perm="dining"><AdminDining /></PermissionRoute>} />
                  <Route path="kitchen" element={<PermissionRoute perm="kitchen"><AdminKitchen /></PermissionRoute>} />
                  <Route path="telegram" element={<PermissionRoute perm="users"><AdminTelegram /></PermissionRoute>} />
                  <Route path="room-history" element={<PermissionRoute perm="housekeeping"><AdminRoomHistory /></PermissionRoute>} />
                  <Route path="room-history/:unitName" element={<PermissionRoute perm="housekeeping"><AdminRoomHistory /></PermissionRoute>} />
                  <Route path="vouchers" element={<PermissionRoute perm="guests"><AdminVouchers /></PermissionRoute>} />
                  <Route path="users" element={<PermissionRoute perm="users"><AdminUsers /></PermissionRoute>} />
                  <Route path="activity" element={<PermissionRoute perm="activity"><AdminActivity /></PermissionRoute>} />
                  <Route path="change-password" element={<ChangePasswordPage />} />
                </Route>

                {/* Public guest pages */}
                <Route path="/feedback" element={<FeedbackForm />} />
                <Route path="/guest" element={<GuestDirectory />} />
                <Route path="/upsell/:token" element={<UpsellPage />} />
                <Route path="/deposit/:token" element={<DepositPage />} />
                <Route path="/precheckin/:token" element={<PrecheckinPage />} />
                <Route path="/pay/:token" element={<DepositPayPage />} />
                <Route path="/review/:token" element={<ReviewPage />} />
                <Route path="/room/:code" element={<RoomHome />} />
                <Route path="/room/:code/request" element={<RoomRequest />} />
                <Route path="/room/:code/survey" element={<RoomSurvey />} />
                <Route path="/r/:code" element={<QRRedirect />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              <MaintenanceOverlay />
              </Suspense>
            </AuthProvider>
          </BookingProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
